import { normalizeIntakeJob } from 'app/services/api/aiDocumentIntakeApi';
import _ from 'lodash';

import { SIGNAL_R_AI_PROCESS_RUNS_CONNECTION_URL } from 'constants/api';
import {
  DocumentIntakeJobStatus,
  DocumentIntakeSignalREventType,
  DocumentIntakeTerminalStatuses,
} from 'constants/documentIntake';
import { logger } from 'utils/logger';

import { loadExecutionFormAction } from 'store/aiProcessRuns/actions';
import { jobUpsertAction } from 'store/documentIntake/actions';
import { showErrorMessageAction, showMessageAction } from 'store/global/actions';

import { aiProcessRunsSignalRConnection, aiProcessRunsSignalRController } from './index';
import { accessTokenFactory } from './signalR.utils';

const lifecycle = { initPromise: null, teardownPromise: null };

const registerConnectionStatusHandlers = dispatch => {
  // DEV-3055: the connection is built withAutomaticReconnect. On reconnect the
  // socket comes back with a NEW connection id, so the server-side group
  // memberships from JoinExecution / JoinLoanApplication are lost and the AI
  // Activity tab silently stops receiving live step/execution events until a
  // manual refresh. Re-join the tracked groups so live updates keep flowing.
  // (aiChatConnector applies the same pattern via resubscribe() — DEV-3061.)
  //
  // Re-joining only restores the live stream going forward; any form_field_set /
  // form_status_changed emitted while the socket was down is lost. Reconcile the
  // tracked executions' forms over REST (force overwrites the possibly-drifted
  // live slice) so the review surface reflects the true server state on resume.
  const rejoinGroups = () => {
    aiProcessRunsSignalRController.resubscribe().catch(error => {
      logger.error('[AiProcessRuns SignalR] Failed to re-join groups after reconnect', error);
    });

    aiProcessRunsSignalRController.getTrackedExecutionIds().forEach(executionId => {
      dispatch(loadExecutionFormAction({ executionId, force: true }));
    });
  };

  aiProcessRunsSignalRConnection.onreconnected(rejoinGroups);
  // DEV-3108: the wake-from-sleep path does a cold start() that does NOT fire
  // onreconnected, so re-join on the wake hook too.
  aiProcessRunsSignalRConnection.onWakeReconnected(rejoinGroups);

  aiProcessRunsSignalRConnection.onclose(error => {
    if (error) {
      logger.error('[AiProcessRuns SignalR] Connection closed with error', error);
    }
  });
};

const runInit = async dispatch => {
  if (lifecycle.teardownPromise) {
    await lifecycle.teardownPromise.catch(_.noop);
  }

  try {
    const didColdStart = !aiProcessRunsSignalRConnection.isConnected;

    if (didColdStart) {
      aiProcessRunsSignalRConnection.init(
        SIGNAL_R_AI_PROCESS_RUNS_CONNECTION_URL,
        accessTokenFactory,
      );
      registerConnectionStatusHandlers(dispatch);

      await aiProcessRunsSignalRConnection.start();
    }

    aiProcessRunsSignalRController.connect({ dispatch });

    // Dispatches a thunk so it can read the pre-upsert job state from the store
    // (for the terminal-transition guard) without a separate getState parameter.
    // The wasTerminal check prevents re-firing the toast on a reconnect poll when
    // the job is already terminal in the store.
    const intakeJobHandler = payload => {
      dispatch((_, getState) => {
        const job = normalizeIntakeJob(payload?.job ?? payload);

        if (!job?.id) {
          return;
        }

        const prevJob = getState().documentIntake?.jobsById?.[job.id];
        const wasTerminal = !!prevJob && DocumentIntakeTerminalStatuses.has(prevJob.status);

        dispatch(jobUpsertAction(job));

        if (!wasTerminal && DocumentIntakeTerminalStatuses.has(job.status)) {
          if (job.status === DocumentIntakeJobStatus.COMPLETED) {
            const count = job.totalSegments ?? 0;

            dispatch(
              showMessageAction({
                message: `Document intake ready — ${count} document${
                  count === 1 ? '' : 's'
                } to review`,
                variant: 'success',
              }),
            );
          }

          if (job.status === DocumentIntakeJobStatus.FAILED) {
            dispatch(
              showErrorMessageAction({
                message: 'Document intake failed — please try again',
              }),
            );
          }
        }
      });
    };

    // Deregister before re-registering to prevent stacking duplicate handlers on
    // a warm re-init (runInit called again without a full teardown cycle).
    Object.values(DocumentIntakeSignalREventType).forEach(eventName => {
      aiProcessRunsSignalRConnection.off(eventName);
      aiProcessRunsSignalRConnection.on(eventName, intakeJobHandler);
    });

    // A consumer (e.g. the execution drawer) can call joinExecution before this
    // cold start resolves, which records the id as pending instead of dropping
    // it. Drain those pending joins now that the socket is Connected — neither
    // onreconnected nor onWakeReconnected fires on a first start.
    if (didColdStart) {
      await aiProcessRunsSignalRController.resubscribe().catch(_.noop);
    }
  } catch (error) {
    logger.error('[AiProcessRuns SignalR] Failed to initialize connection', error);
    lifecycle.initPromise = null;

    throw error;
  }
};

const runTeardown = async () => {
  if (lifecycle.initPromise) {
    await lifecycle.initPromise.catch(_.noop);
  }

  aiProcessRunsSignalRController.disconnect();

  Object.values(DocumentIntakeSignalREventType).forEach(eventName => {
    aiProcessRunsSignalRConnection.off(eventName);
  });

  if (aiProcessRunsSignalRConnection.isConnected) {
    try {
      await aiProcessRunsSignalRConnection.stop();
    } catch (error) {
      logger.error('[AiProcessRuns SignalR] Failed to stop connection', error);
    }
  }

  aiProcessRunsSignalRConnection.resetConnection();
  lifecycle.initPromise = null;
};

export const initAiProcessRunsConnection = dispatch => {
  if (lifecycle.teardownPromise) {
    const pendingTeardown = lifecycle.teardownPromise;

    lifecycle.initPromise = pendingTeardown.catch(_.noop).then(() => runInit(dispatch));

    return lifecycle.initPromise;
  }

  if (lifecycle.initPromise) {
    return lifecycle.initPromise;
  }

  lifecycle.initPromise = runInit(dispatch);

  return lifecycle.initPromise;
};

export const teardownAiProcessRunsConnection = () => {
  lifecycle.teardownPromise = runTeardown().finally(() => {
    lifecycle.teardownPromise = null;
  });

  return lifecycle.teardownPromise;
};
